import { Navigate, Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getModuleForPath, hasReadPermission } from '@/lib/rbac';

// Pantalla mínima para el caso borde de un usuario sin permiso ni siquiera
// sobre el dashboard (evita un bucle de redirección hacia /admin).
function AccessDeniedScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 gap-4 px-4">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
        <i className="ri-shield-user-line text-red-600 text-xl"></i>
      </div>
      <p className="text-base font-semibold text-foreground-900">Acceso Denegado</p>
      <p className="text-sm text-foreground-500 text-center">No tienes permisos para acceder a esta sección.</p>
      <Link
        to="/login"
        className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap"
      >
        Volver al inicio de sesión
      </Link>
    </div>
  );
}

// Protege todas las rutas bajo /admin/*. Sin sesión activa, redirige a /login.
// Con sesión, aplica RBAC: cada ruta requiere permiso de lectura del módulo
// correspondiente; si no lo tiene, redirige a /admin con alerta "Acceso Denegado".
export default function AuthGuard() {
  const { session, loading, profile, profileLoading } = useAuth();
  const location = useLocation();

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 gap-4">
        <div className="w-9 h-9 rounded-full border-2 border-accent-300 border-t-accent-500 animate-spin"></div>
        <p className="text-sm text-foreground-500">Cargando sesión…</p>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Enforcement RBAC.
  const module = getModuleForPath(location.pathname);
  const allowed =
    !!module && hasReadPermission(profile?.permissions ?? {}, module, profile?.role ?? '');

  if (!allowed) {
    // Ya en el dashboard sin permiso: muestra pantalla en línea (evita bucle).
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      return <AccessDeniedScreen />;
    }
    return <Navigate to="/admin" state={{ accessDenied: true }} replace />;
  }

  return <Outlet />;
}